import { createPortal } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import { useBookEdits } from '../book/actions';
import { loadBook } from '../book/load';
import { MovePlaceSheet, StartSheet } from '../book/reading';
import { NotForMeSheet } from '../book/BookView';
import { PastReadSheet } from '../book/edit';
import { useNotice } from '../shell/notice';
import type { BookData } from '../book/types';

// A Queue row's Move ▾ uses the book page's own moves and sheets, as Shelves' cards do (Kristi,
// Oct 29: the same Move pattern everywhere): Currently Reading opens Start reading; Up Next and
// Future reads ask where on the shelf; Finished… adds a past read; Not for me… asks why;
// Reference moves at once and asks "Should it be Study?". The book is read in full only then.

export type BookSheetKind = { kind: 'start' } | { kind: 'place'; to: 'up_next' | 'future_reads' } | { kind: 'not_for_me' } | { kind: 'past' } | { kind: 'reference' };

export function BookSheet({ label, title, what, onClose }: { label: number; title: string; what: BookSheetKind; onClose: () => void }) {
  const notice = useNotice();
  const [book, setBook] = useState<BookData | null>(null);
  const edits = useBookEdits(book, setBook, () => loadBook(label));
  useEffect(() => {
    let live = true;
    loadBook(label)
      .then((b) => {
        if (!live) return;
        if (b) setBook(b);
        else {
          notice({ message: `Couldn’t open ${title}`, done: false });
          onClose();
        }
      })
      .catch((e: unknown) => {
        notice({ message: `Couldn’t open ${title}: ${e instanceof Error ? e.message : String(e)}`, done: false });
        onClose();
      });
    return () => {
      live = false;
    };
  }, [label]);
  // Reference needs no sheet: the book page's own move, with its Study question in the notice.
  useEffect(() => {
    if (book && what.kind === 'reference') void edits.move('reference').finally(onClose);
  }, [book]);
  if (!book || what.kind === 'reference') return null;
  return createPortal(
    <div class="shelf-sheet">
      {what.kind === 'start' ? <StartSheet book={book} edits={edits} onClose={onClose} /> : null}
      {what.kind === 'place' ? <MovePlaceSheet book={book} to={what.to} edits={edits} onClose={onClose} /> : null}
      {what.kind === 'not_for_me' ? <NotForMeSheet book={book} edits={edits} onClose={onClose} /> : null}
      {what.kind === 'past' ? <PastReadSheet book={book} edits={edits} onClose={onClose} move /> : null}
    </div>,
    document.body,
  );
}
